import { Chat } from "@phosphor-icons/react";
import { Fragment } from "react";

import RenderIf from "common/components/RenderIf";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import { useCampaignContext } from "../../../context/CampaignContext";
import { useCampaignFlowContext } from "../../../context/CampaignFlowContext";
import useFlowMessages from "../../../datahooks/useFlowMessages";
import ActionDelay from "./ActionDelay";
import FlowTemplate from "./FlowTemplate";
import Placeholder from "./Placeholder";
import SectionHeading from "./SectionHeading";

import type { FlowMessageTemplate } from "../../../types";

interface MessageTemplatesProps {
  messageTemplates: FlowMessageTemplate[];
}

export default function MessageTemplates({
  messageTemplates,
}: MessageTemplatesProps) {
  const isDesktop = useTwBreakpoint("lg");
  const { flow, isFlowError } = useCampaignFlowContext();

  const {
    addFlowMessage,
    deleteLastFlowMessage,
    updateFlowMessage,
    addPlaceholderMessage,
    removePlaceholderMessage,
  } = useFlowMessages();

  const { outreach_type: outreachType } = useCampaignContext();
  const isPlaceholderDisabled = messageTemplates.some(
    (template) => !template.original_id,
  );

  const errorTextComponent = (
    <p className="-mt-2 text-xs text-red-500">
      You must add at least one message template
    </p>
  );

  function addMessageTemplate(flowTemplate: FlowMessageTemplate) {
    addFlowMessage({
      flowMessage: flowTemplate,
    });
  }

  function setMessageTemplate(
    index: number,
    flowTemplate: FlowMessageTemplate,
  ) {
    updateFlowMessage({
      index,
      updates: flowTemplate,
    });
  }

  function setMessageDelay(index: number, delay: number) {
    updateFlowMessage({
      index,
      updates: {
        delay,
      },
    });
  }

  const isDrip = outreachType === "drip";

  return (
    <>
      <SectionHeading
        title={`${isDrip ? "Drip " : ""}Message Sequence`}
        description={
          isDrip
            ? "Send a Direct Message to your campaign targets"
            : "Send a follow-up sequence after targets accept your connection request. Sequences stop when a lead replies"
        }
        icon={<Chat />}
        shouldShowMobileTooltip={false}
      />

      <div className="flex flex-col gap-4">
        {messageTemplates.map((messageTemplate, index, { length }) => {
          const isLastItem = index + 1 === length;
          const isValidTemplate = messageTemplate.original_id;
          const shouldHideDelay =
            index === 0 && flow.type === "PRIMARY_MESSAGE";

          const actionDelayComponent = (
            <ActionDelay
              delay={messageTemplate.delay * 3600000}
              onDelayChange={(delay) => {
                setMessageDelay(index, delay / 3600000);
              }}
              isDisabled={!isValidTemplate}
              labels={{
                action: isDesktop ? "Message will be sent" : "sending message",
                after: index === 0 ? "connecting" : `message ${index}`,
              }}
            />
          );

          return (
            <Fragment key={messageTemplate.original_id || index}>
              <FlowTemplate
                flowTemplate={messageTemplate}
                setFlowTemplate={(newTemplate) => {
                  if (isValidTemplate) {
                    setMessageTemplate(
                      index,
                      newTemplate as FlowMessageTemplate,
                    );
                  } else {
                    addMessageTemplate(newTemplate as FlowMessageTemplate);
                  }
                }}
                handleRemove={
                  isValidTemplate
                    ? deleteLastFlowMessage
                    : removePlaceholderMessage
                }
                isRemoveButtonHidden={!isLastItem || shouldHideDelay}
                actionDelayComponent={!shouldHideDelay && actionDelayComponent}
              />
              <RenderIf condition={isFlowError}>{errorTextComponent}</RenderIf>
            </Fragment>
          );
        })}

        <RenderIf condition={messageTemplates.length < 10}>
          <Placeholder
            onAdd={addPlaceholderMessage}
            templateType="MESSAGE_TEMPLATE"
            isDisabled={isPlaceholderDisabled}
          />
        </RenderIf>

        <RenderIf condition={isFlowError && !messageTemplates.length}>
          {errorTextComponent}
        </RenderIf>
      </div>
    </>
  );
}
